// import external libraries
import { useTranslation } from 'react-i18next';

// import components
import BacklogFilterMultiSelect from '../BacklogFilterMultiSelect';

// import hooks
import { useBacklogFunctionOptions } from './BacklogFunctionFilter.hooks';

// import types
import type { FilterOption } from '../../../../Backlog.types';
import type { BacklogFunctionFilterProps } from './BacklogFunctionFilter.types';

/**
 * Every roster function valid in the takenlijst's period.
 *
 * Not narrowed by the picked plan units, and no longer pretending to be: a roster function is a
 * tenant-wide record — `routes/roster/Functions.groovy` (`getFunctions`) reads only the two dates — so
 * the field neither sends a plan unit nor waits for one. It is usable as soon as the popover is open,
 * which is also when its one call is made; see {@link useBacklogFunctionOptions}.
 */
const BacklogFunctionFilter = ({
  startDate,
  endDate,
  selected,
  onChange
}: BacklogFunctionFilterProps) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'backlog' });

  const { data = [] } = useBacklogFunctionOptions(startDate, endDate);

  const options: FilterOption[] = data.map(({ id, description }) => ({
    id: String(id),
    name: description
  }));

  return (
    <BacklogFilterMultiSelect<FilterOption>
      label={t('filters.function')}
      options={options}
      selected={selected}
      onChange={onChange}
      getOptionLabel={({ name }) => name}
      isOptionEqualToValue={(option, picked) => option.id === picked.id}
      // No onQueryChange: the whole list arrived in one call, so the control filters it locally.
    />
  );
};

export default BacklogFunctionFilter;
